import React, { useState } from 'react';
import { Alert, ConfigService, UserService } from '../../services';
import ManualKycModal from './ManualKycModal';
import { getUserDetails } from '../../services/user.service';
import KycLinkModal from './KycLinkModal';
import {
  KycFailedIcon,
  KycInprogressIcon,
  KycReviewIcon,
  KycVerifiedIcon,
  OnButtonLoaderIcon,
  WalletIcon,
} from '../icons/IconProvider';

const ViewUser = ({
  userDetails,
  handleEditUser,
  handleSelectChange,
  isAdmin,
  userId,
  isEdit,
  refreshUserDetails,
  kycReasons,
}) => {
  const [allWallets, setAllWallets] = useState(false);
  const [showManualKycModal, setShowManualKycModal] = useState(false);
  const [showKycLinkModal, setShowKycLinkModal] = useState(false);
  const [kycLinkData, setKycLinkData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Handle manual KYC approval
  const handleManualKycSubmit = async ({ userId, manualKycNote }) => {
    try {
      const response = await UserService.updateKycStatus({
        userId,
        manualKycNote,
      });
      if (response.status === 'failure') {
        Alert.error(response.message);
        return;
      }
      Alert.success(response.message);
      setShowManualKycModal(false);
      // Refresh user details to reflect the updated KYC status
      if (refreshUserDetails) {
        await refreshUserDetails(userId);
      }
      await getUserDetails(userId);
    } catch (error) {
      Alert.error(error.message || 'Failed to process KYC');
    }
  };

  const handleGenerateKycLink = async () => {
    try {
      setIsLoading(true);
      const response = await UserService.generateKycLink(userId);
      setKycLinkData(response.data);
      setShowKycLinkModal(true);
    } catch (error) {
      Alert.error(error.message || 'Failed to generate KYC link');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetApplicant = async () => {
    try {
      setIsLoading(true);
      await UserService.resetApplicant(userDetails);
      Alert.success('KYC reset successfully.');

      refreshUserDetails && (await refreshUserDetails(userId));
    } catch (error) {
      Alert.error(error.message || 'Failed to reset KYC');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full bg-brandGray rounded-xl p-5 mb-4">
      <div className="mb-6">
        <div className="lg:flex lg:items-start lg:gap-6 lg:justify-between">
          <div className="md:flex md:items-center md:gap-6">
            <div className="w-28 h-28 min-w-[112px] rounded-full block overflow-hidden mb-4 md:mb-0">
              <img
                alt=""
                src={
                  userDetails?.profilePicture
                    ? userDetails?.profilePicture
                    : '/img/profile-avatar.png'
                }
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/img/profile-avatar.png';
                }}
                className="object-cover w-full h-full"
              />
            </div>

            <div className="w-full lg:grid lg:grid-cols-3 lg:gap-6 lg:max-w-xl">
              <div className="col-span-1 mb-4 lg:mb-0">
                <label className="font-semibold">Member Since</label>
                <p className="text-gray-500">
                  {ConfigService.dateFormat(userDetails.created_at, false)}
                </p>
              </div>

              <div className="col-span-2 mb-4 lg:mb-0">
                <label className="font-semibold">Primary Wallet:</label>
                <div className="flex items-center">
                  <p className="text-sm mr-3 break-all min-w-[220px]">
                    {userDetails && userDetails.userWallets?.length > 0
                      ? userDetails.userWallets.find(
                          (userWallet) => userWallet.type === 'platform',
                        )?.wallet_address
                      : 'Platform wallet yet to be created'}
                  </p>
                  <button
                    type="button"
                    onClick={() => setAllWallets(true)}
                    className="py-1 px-3 text-sm bg-brandBlue text-white rounded-md whitespace-nowrap"
                  >
                    See All
                  </button>
                </div>

                {allWallets && (
                  <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity opacity-100 z-10 flex items-center justify-center">
                    <div className="relative bg-white w-[600px] max-w-full rounded-2xl p-4">
                      <div className="flex items-center justify-between pb-4">
                        <h5 className="text-xl font-semibold">
                          Wallet Addresses
                        </h5>
                        <button
                          className="w-7 h-7 flex items-center justify-center p-0"
                          onClick={() => setAllWallets(false)}
                        >
                          <WalletIcon />
                        </button>
                      </div>
                      <div className="w-full border-t border-gray-200 pt-4">
                        {userDetails && userDetails.userWallets?.length > 0 ? (
                          userDetails.userWallets.map((userWallet) => (
                            <div
                              key={userWallet?.wallet_address}
                              className="py-3 px-4 rounded-md border border-gray-200 text-sm flex items-center justify-between mb-2"
                            >
                              <p>{userWallet?.wallet_address || ''}</p>
                              {userWallet?.type === 'platform' && (
                                <span className="py-1 px-2 rounded text-xs bg-brandBlue text-white">
                                  Energyblocks Wallet
                                </span>
                              )}
                            </div>
                          ))
                        ) : (
                          <div className="py-3 px-4 rounded-md border border-gray-200 text-sm flex items-center justify-between mb-2">
                            <p>No wallets connected yet</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="col-span-1 mb-4 lg:mb-0">
                <label className="font-semibold block">KYC Status</label>
                <div className="flex flex-col gap-2 text-sm">
                  {userDetails?.kyc_status === 0 ? (
                    <div className="inline-flex items-center text-grey-700 text-xs font-semibold">
                      <KycInprogressIcon />
                      KYC not started
                    </div>
                  ) : userDetails?.kyc_status === 1 ? (
                    <div className="inline-flex items-center text-green-700 text-xs font-semibold">
                      <KycVerifiedIcon />
                      KYC Verified
                    </div>
                  ) : userDetails?.kyc_status === 2 ? (
                    <div className="inline-flex items-center text-red-700 text-xs font-semibold">
                      <KycFailedIcon />
                      KYC Failed
                    </div>
                  ) : userDetails?.kyc_status === 3 ? (
                    <div className="bg-brandOrange text-yellow-500 py-2 px-3 rounded w-auto inline-flex items-center">
                      <KycInprogressIcon />
                      <p className="leading-tight text-sm font-semibold">
                        KYC In-Process
                      </p>
                    </div>
                  ) : userDetails?.kyc_status === 4 ? (
                    <div className="bg-brandOrange text-yellow-500 py-2 px-3 rounded w-auto inline-flex items-center">
                      <KycReviewIcon />
                      <p className="leading-tight text-sm font-semibold">
                        KYC In Review
                      </p>
                    </div>
                  ) : (
                    '--'
                  )}

                  {userDetails?.kyc_status === 0 && (
                    <div className="flex gap-3 mt-2">
                      {userDetails?.kyc_status === 0 && (
                        <button
                          type="button"
                          onClick={() => setShowManualKycModal(true)}
                          className="py-1 px-3 text-sm bg-brandBlue text-white rounded-md whitespace-nowrap"
                        >
                          Manually approve KYC
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={handleGenerateKycLink}
                        disabled={isLoading}
                        className={`py-1 px-3 text-sm bg-brandBlue text-white rounded-md whitespace-nowrap ${
                          isLoading ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        {isLoading ? (
                          <OnButtonLoaderIcon />
                        ) : (
                          'Generate KYC Link'
                        )}
                      </button>
                    </div>
                  )}
                  {(userDetails?.kyc_status === 3 ||
                    userDetails?.kyc_status === 2) && (
                    <div className="text-center my-2">
                      <button
                        className="py-1 px-3 text-sm bg-brandBlue text-white rounded-md whitespace-nowrap"
                        onClick={handleResetApplicant}
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <OnButtonLoaderIcon />
                        ) : (
                          'Reset KYC Profile'
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {kycReasons?.length > 0 && (
                <div className="col-span-2 mb-4 lg:mb-0">
                  <label className="font-semibold block">KYC Reason</label>
                  <span className="text-sm">
                    {userDetails?.kyc_status === 1 ? (
                      <div className="bg-brandRed text-brandRed  px-3 rounded w-auto inline-flex items-center">
                        <ul className="max-w-[340px] mx-auto">
                          {kycReasons?.length > 0 &&
                            kycReasons?.map((reason, index) => {
                              return (
                                <li
                                  key={index}
                                  className="flex gap-2 items-start text-sm text-gray-600 mb-2 relative pl-4"
                                >
                                  <div className="w-1.5 h-1.5 rounded-full bg-gray-400 absolute left-0 top-2"></div>
                                  {reason}{' '}
                                </li>
                              );
                            })}
                        </ul>
                      </div>
                    ) : (
                      '--'
                    )}
                  </span>
                </div>
              )}
            </div>
          </div>

          {!isEdit && (
            <div className="flex gap-3 justify-end lg:justify-normal">
              <button
                onClick={() => handleSelectChange(!isAdmin ? 0 : 1)}
                className="bg-brandBlue font-semibold text-white border border-brandBlue whitespace-nowrap inline-flex items-center text-sm mb-3 md:mb-0 px-4 py-2 rounded-md"
              >
                {isAdmin ? 'Remove Admin' : 'Make Admin'}
              </button>
              <button
                onClick={() => handleEditUser(userId)}
                className="bg-[#99D94A] font-semibold text-brandBlue border border-[#99D94A] whitespace-nowrap inline-flex items-center text-sm mb-3 md:mb-0 px-4 py-2 rounded-md"
              >
                Edit
              </button>
            </div>
          )}
        </div>
      </div>

      <ManualKycModal
        isOpen={showManualKycModal}
        onClose={() => setShowManualKycModal(false)}
        onSubmit={handleManualKycSubmit}
        userDetails={userDetails}
        userId={userId}
      />
      <KycLinkModal
        isOpen={showKycLinkModal}
        onClose={() => setShowKycLinkModal(false)}
        kycLink={kycLinkData?.url}
        expiry={kycLinkData?.expiry}
      />
    </div>
  );
};

export default ViewUser;
